/**
 * The classic 4-channel BrainBit (`T3, T4, O1, O2`) over raw BLE, through Web
 * Bluetooth.
 *
 * The protocol is the one `calibrations/backend/_reference/brainbit_bleak_adapter.py`
 * drives from Python: a Nordic-UART-shaped service, a status characteristic
 * that must be subscribed first, `CMD_STOP` then `CMD_START` on TX, and 20-byte
 * notifications on RX each carrying TWO samples of four 20-bit values. Packet
 * order is O1, T3, T4, O2; the device layer reports `[T3, T4, O1, O2]`.
 *
 * CONTACT QUALITY IS MEASURED, with the signal estimate as the fallback (V1-CLOSURE
 * R5, ruling R70; `docs/classic-resistance.md`). `startResist` runs the vendor's
 * resistance cycle (`classicResistance.ts`, from `web-neurosdk-brainbit` 2.1.0)
 * and emits measured kΩ with `estimated: false`. Whenever that fails — a command
 * write rejects, the headband refuses or reports a state it cannot measure from,
 * the data is malformed, or no reading arrives in time — it falls back, for the
 * rest of the connection, to exactly the pre-R5 behaviour: stream signal and
 * derive contact quality with `SignalQualityEstimator` (`estimated: true`, and
 * the page says so). The measured readout is OPT-IN per browser until it is
 * verified on hardware: `localStorage['brainspot:classic-resistance'] = 'measured'`
 * turns it on; without it the driver is the pre-R5 path from the first byte.
 *
 * RESISTANCE MODE NEVER REACHES A RECORDING. While measuring, RX frames are
 * contact measurements and are never emitted as samples; `startSignal` leaves
 * resistance mode (STOP) and restores signal mode (START), and resolves only
 * once a signal frame has arrived. The capture page runs a transition's effects
 * in order (`runEffectsInOrder`), so the recorder starts after that.
 */

import { STALE_AFTER_MS } from '../capture/quality'
import { ResistanceCycle, type CycleOutput, type ResistContact } from './classicResistance'
import { Emitter, HeadbandError, webBluetoothAvailable, type Headband, type HeadbandEvents, type HeadbandInfo } from './types'
import { SignalQualityEstimator } from './signalQuality'

export const SERVICE_UUID = '6e400001-b534-f393-68a9-e50e24dcca9e'
export const STATUS_UUID = '6e400002-b534-f393-68a9-e50e24dcca9e'
export const TX_UUID = '6e400003-b534-f393-68a9-e50e24dcca9e'
export const RX_UUID = '6e400004-b534-f393-68a9-e50e24dcca9e'
export const CMD_START = new Uint8Array([0x02, 0x00, 0x00, 0x00, 0x00])
export const CMD_STOP = new Uint8Array([0x01])
/** ADC counts → µV: 2.4 V reference over a 20-bit range at gain 6. */
export const CALIBRATION = (2.4 * 1_000_000) / (0xfffff * 6)
export const CLASSIC_CHANNELS = ['T3', 'T4', 'O1', 'O2'] as const
export const SAMPLING_RATE = 250
/** How often the estimator publishes a `resist` reading while estimating. */
const RESIST_PERIOD_MS = 500
/** The measured readout must deliver its first full reading within this (STOP and settle included), or the estimate takes over. */
export const MEASURE_FIRST_READING_MS = 5000
/**
 * …and every later reading within this. Under the fitting gate's
 * `STALE_AFTER_MS`, so a readout too slow to keep the gate fresh falls back to
 * the estimate instead of holding Ready away.
 */
export const MEASURE_GAP_MS = STALE_AFTER_MS - 1000
/** STOP → next command: the reference adapter's 0.5 s spacing, and calibrations' `MODE_SETTLE_SECONDS`. */
const MODE_SETTLE_MS = 500
/** Leaving resistance mode: how long to wait for the first signal frame after START (the same 2 s as `ensureStreaming`). */
const SIGNAL_CONFIRM_MS = 2000
/** A resistance command still in flight when measuring stops gets this long before STOP is written anyway. */
const INFLIGHT_GRACE_MS = 1000
/**
 * The per-browser opt-in: `'measured'` turns the measured readout on. Until the
 * readout is verified on a physical classic headband (R70), every other browser
 * keeps the pre-R5 estimate, so an unproven mode switch never sits in front of a
 * recording by default.
 */
export const RESISTANCE_PREFERENCE_KEY = 'brainspot:classic-resistance'

/** True only when this browser opted in with `'measured'`. Storage that throws counts as no opt-in. */
export function measuredResistancePreferred(storage?: Pick<Storage, 'getItem'> | null): boolean {
  try {
    const s = storage === undefined ? (typeof localStorage === 'undefined' ? null : localStorage) : storage
    return s?.getItem(RESISTANCE_PREFERENCE_KEY) === 'measured'
  } catch {
    return false
  }
}

function sign32(v: number): number {
  return v & 0x80000000 ? v - 0x100000000 : v
}

/**
 * Decode a 20-byte notification into two samples, `[T3, T4, O1, O2]` in µV.
 * Bit-for-bit the reference adapter's `decode_brainbit_packet`.
 */
export function decodeBrainBitPacket(data: Uint8Array | DataView): [number[], number[]] {
  const d = data instanceof DataView ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength) : data
  if (d.length < 20) throw new HeadbandError('protocol', `expected a 20-byte packet, got ${d.length}`)
  const b = (i: number) => d[i]!

  // First sample (bit-packed across bytes 1-11).
  const eeg01 = sign32((((b(1) & 0x0f) << 28) | (b(2) << 20) | (b(3) << 12) | (b(4) << 4)) >>> 0) / 2048.0
  const eeg02 = sign32((((b(4) & 0x7f) << 25) | (b(5) << 17) | (b(6) << 9) | (b(7) << 1)) >>> 0) / 2048.0
  const eeg03 = sign32((((b(6) & 0x03) << 30) | (b(7) << 22) | (b(8) << 14) | (b(9) << 6)) >>> 0) / 2048.0
  const eeg04 = sign32((((b(9) & 0x1f) << 27) | (b(10) << 19) | (b(11) << 11)) >>> 0) / 2048.0

  // Second sample (delta-encoded, bytes 12-19).
  const eeg11 = eeg01 + sign32(((b(12) << 24) | (b(13) << 16)) >>> 0) / 65536.0
  const eeg12 = eeg02 + sign32(((b(14) << 24) | (b(15) << 16)) >>> 0) / 65536.0
  const eeg13 = eeg03 + sign32(((b(16) << 24) | (b(17) << 16)) >>> 0) / 65536.0
  const eeg14 = eeg04 + sign32(((b(18) << 24) | (b(19) << 16)) >>> 0) / 65536.0

  // BLE order: eeg01=O1, eeg02=T3, eeg03=T4, eeg04=O2 → output T3, T4, O1, O2.
  const r4 = (v: number) => Math.round(v * CALIBRATION * 1e4) / 1e4
  return [
    [r4(eeg02), r4(eeg03), r4(eeg01), r4(eeg04)],
    [r4(eeg12), r4(eeg13), r4(eeg11), r4(eeg14)],
  ]
}

/**
 * Build a packet the decoder reads back as the given two samples of ADC
 * counts (`[O1, T3, T4, O2]` order, first sample absolute 20-bit signed, second
 * as a signed 16-bit delta in 1/65536 units of the first's 1/2048 scale).
 * Exists so the decoder can be tested against known bit patterns without a
 * captured trace; not used at runtime.
 */
export function encodeBrainBitPacket(first: [number, number, number, number], deltas: [number, number, number, number]): Uint8Array {
  const p = new Uint8Array(20)
  const u20 = (v: number) => v & 0xfffff
  const [a, b, c, e] = first.map(u20) as [number, number, number, number]
  // Layout mirrors the decoder's bit positions.
  // eeg01: bits 19..16 in d1 low nibble, 15..8 in d2, 7..0 in d3, and d4 high nibble holds bits -1..-4 (zero here).
  p[1] = (a >>> 16) & 0x0f
  p[2] = (a >>> 8) & 0xff
  p[3] = a & 0xff
  // eeg02: 32-bit word = (d4&0x7f)<<25 | d5<<17 | d6<<9 | d7<<1 ; value = word>>12 → 20 bits at 31..12.
  const w2 = (b << 12) >>> 0
  p[4] = (p[4]! & 0x80) | ((w2 >>> 25) & 0x7f)
  p[5] = (w2 >>> 17) & 0xff
  p[6] = (w2 >>> 9) & 0xff
  p[7] = (w2 >>> 1) & 0xff
  // eeg03: word = (d6&0x03)<<30 | d7<<22 | d8<<14 | d9<<6
  const w3 = (c << 12) >>> 0
  p[6] = (p[6]! & 0xfc) | ((w3 >>> 30) & 0x03)
  p[7] = (p[7]! & 0x00) | ((w3 >>> 22) & 0xff)
  p[8] = (w3 >>> 14) & 0xff
  p[9] = (w3 >>> 6) & 0xff
  // eeg04: word = (d9&0x1f)<<27 | d10<<19 | d11<<11
  const w4 = (e << 12) >>> 0
  p[9] = (p[9]! & 0xe0) | ((w4 >>> 27) & 0x1f)
  p[10] = (w4 >>> 19) & 0xff
  p[11] = (w4 >>> 11) & 0xff
  deltas.forEach((dv, i) => {
    const u = dv & 0xffff
    p[12 + i * 2] = (u >>> 8) & 0xff
    p[13 + i * 2] = u & 0xff
  })
  return p
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))
const reasonOf = (err: unknown) => (err as Error)?.message ?? String(err)

/** The subset of Web Bluetooth this driver touches, so a test can hand in a double. */
export interface BluetoothLike {
  requestDevice(options: RequestDeviceOptions): Promise<BluetoothDevice>
}

export interface ClassicOptions {
  /** Try the measured readout first. Defaults to `measuredResistancePreferred()`. */
  measured?: boolean
}

/** One measured-resistance attempt, from its STOP to its end. */
interface MeasureRun {
  readonly cycle: ResistanceCycle
  /** True once the first resist command has been written: status frames go to the cycle from then on. */
  commanded: boolean
  /** The first-reading watchdog, then the gap watchdog. */
  timer: ReturnType<typeof setTimeout> | null
  /** The GATT writes this run has started, chained. Never rejects. */
  inflight: Promise<void>
  /** Settle the wait for the first reading; both null once it has settled. */
  resolveFirst: ((outcome: 'reading' | 'cancelled') => void) | null
  rejectFirst: ((err: Error) => void) | null
  readonly first: Promise<'reading' | 'cancelled'>
}

export class WebBluetoothClassicBrainBit implements Headband {
  private readonly emitter = new Emitter()
  private device: BluetoothDevice | null = null
  private tx: BluetoothRemoteGATTCharacteristic | null = null
  private rx: BluetoothRemoteGATTCharacteristic | null = null
  private status: BluetoothRemoteGATTCharacteristic | null = null
  private streaming = false
  private estimating = false
  private estimator: SignalQualityEstimator | null = null
  private resistTimer: ReturnType<typeof setInterval> | null = null
  private pack = 0
  private packets = 0
  private lastPacketAt: number | null = null
  private statusFrames = 0
  private lastStatusHex: string | null = null
  private _info: HeadbandInfo | null = null
  private readonly bluetooth: BluetoothLike | null
  private readonly measuredWanted: boolean
  /** The measured attempt in progress, or null. */
  private measure: MeasureRun | null = null
  /** Latched when a measured attempt fails: the rest of this connection uses the estimate. */
  private measuredFailed = false
  /** A fallback in progress; the public calls wait for it rather than race its GATT writes. */
  private transition: Promise<void> | null = null

  constructor(bluetooth?: BluetoothLike, options: ClassicOptions = {}) {
    this.bluetooth = bluetooth ?? (webBluetoothAvailable() ? navigator.bluetooth : null)
    this.measuredWanted = options.measured ?? measuredResistancePreferred()
  }

  get info(): HeadbandInfo | null {
    return this._info
  }

  on<K extends keyof HeadbandEvents>(event: K, handler: (payload: HeadbandEvents[K]) => void): () => void {
    return this.emitter.on(event, handler)
  }

  async connect(): Promise<HeadbandInfo> {
    if (!this.bluetooth) throw new HeadbandError('unsupported', 'Web Bluetooth is not available in this browser.')
    let device: BluetoothDevice
    try {
      device = await this.bluetooth.requestDevice({
        filters: [{ namePrefix: 'BrainBit' }, { services: [SERVICE_UUID] }],
        optionalServices: [SERVICE_UUID],
      })
    } catch (err) {
      const name = (err as { name?: string })?.name
      if (name === 'NotFoundError') throw new HeadbandError('cancelled', 'No headband was chosen.')
      throw new HeadbandError('connect_failed', (err as Error)?.message ?? 'Could not open the Bluetooth chooser.')
    }
    this.device = device
    this.measuredFailed = false
    device.addEventListener('gattserverdisconnected', this.onDisconnected)
    try {
      const server = await device.gatt!.connect()
      const service = await server.getPrimaryService(SERVICE_UUID)
      this.status = await service.getCharacteristic(STATUS_UUID)
      this.tx = await service.getCharacteristic(TX_UUID)
      this.rx = await service.getCharacteristic(RX_UUID)
      const p = (c: BluetoothRemoteGATTCharacteristic) =>
        (['read', 'write', 'writeWithoutResponse', 'notify', 'indicate'] as const).filter((k) => c.properties[k]).join(',')
      console.debug('[brainbit] characteristics', { status: p(this.status), tx: p(this.tx), rx: p(this.rx) })
      // The firmware wants STATUS subscribed first, then RX, then STOP before
      // START, each step ~0.5 s apart -- the reference adapter's `_ble_main`
      // found the device silently ignores CMD_START otherwise. Same timings.
      await delay(500)
      this.status.addEventListener('characteristicvaluechanged', this.onStatus)
      await this.status.startNotifications()
      await delay(500)
      this.rx.addEventListener('characteristicvaluechanged', this.onPacket)
      await this.rx.startNotifications()
      await delay(500)
    } catch (err) {
      await this.teardown()
      throw new HeadbandError('connect_failed', (err as Error)?.message ?? 'The headband did not answer.')
    }
    this._info = {
      family: 'brainbit',
      name: device.name ?? 'BrainBit',
      channels: CLASSIC_CHANNELS,
      serial: device.name?.replace(/^BrainBit[_ -]*/i, '') || null,
      battery: null, // the raw BLE status frame is not decoded yet (§5.1)
      sensFamily: 'LEBrainBit',
      estimatedQuality: true,
      samplingRate: SAMPLING_RATE,
    }
    this.emitter.emit('status', { at: performance.now(), connected: true, battery: null })
    return this._info
  }

  private readonly onDisconnected = () => {
    this.streaming = false
    this.cancelMeasure()
    this.stopEstimating()
    this.emitter.emit('status', { at: performance.now(), connected: false, battery: null })
  }

  private readonly onStatus = (ev: Event) => {
    const value = (ev.target as BluetoothRemoteGATTCharacteristic).value
    if (!value) return
    this.statusFrames++
    this.lastStatusHex = Array.from(new Uint8Array(value.buffer, value.byteOffset, value.byteLength), (b) => b.toString(16).padStart(2, '0')).join(' ')
    if (this.statusFrames <= 3) console.debug('[brainbit] status frame', this.lastStatusHex)
    const run = this.measure
    if (run?.commanded) {
      try {
        run.cycle.onStatus(value)
      } catch (err) {
        this.measureFailed(run, reasonOf(err))
      }
    }
  }

  private readonly onPacket = (ev: Event) => {
    const value = (ev.target as BluetoothRemoteGATTCharacteristic).value
    if (!value) return
    this.packets++
    this.lastPacketAt = performance.now()
    if (this.packets === 1) console.debug('[brainbit] first RX packet', value.byteLength, 'bytes')
    // Resistance mode: the frame is a contact measurement, never a sample.
    if (this.measure) {
      this.onResistPacket(this.measure, value)
      return
    }
    let decoded: [number[], number[]]
    try {
      decoded = decodeBrainBitPacket(value)
    } catch {
      return
    }
    const at = performance.now()
    const samples = decoded.map((values) => ({ pack: this.pack++, values }))
    if (this.estimator) for (const s of samples) this.estimator.push(s.values)
    if (this.streaming) this.emitter.emit('samples', { at, samples })
  }

  private async write(cmd: Uint8Array): Promise<void> {
    if (!this.tx) throw new HeadbandError('protocol', 'not connected')
    // bleak's `response=False` on macOS silently falls back to a write WITH
    // response when the characteristic does not advertise the other mode;
    // Chrome does not. Pick what the characteristic actually supports, and
    // prefer the acknowledged write when both are there.
    const props = this.tx.properties
    const bytes = cmd as unknown as BufferSource
    if (props.write) await this.tx.writeValueWithResponse(bytes)
    else if (props.writeWithoutResponse) await this.tx.writeValueWithoutResponse(bytes)
    else await this.tx.writeValue(bytes)
    console.debug('[brainbit] wrote', Array.from(cmd).map((b) => b.toString(16).padStart(2, '0')).join(' '), 'via', props.write ? 'write' : props.writeWithoutResponse ? 'writeWithoutResponse' : 'writeValue')
  }

  private async ensureStreaming(): Promise<void> {
    if (this.streaming || this.estimating) return
    const before = this.packets
    await this.write(CMD_STOP)
    await delay(500)
    await this.write(CMD_START)
    // The reference notes a stale-state quirk where the device ignores the
    // first START. One retry, said out loud, before anyone stares at a flat line.
    await delay(2000)
    if (this.packets === before) {
      console.warn('[brainbit] no RX packet 2 s after START; sending STOP/START once more')
      await this.write(CMD_STOP)
      await delay(500)
      await this.write(CMD_START)
    }
  }

  /** Diagnostics for the Headband card: how many frames have arrived, and when the last did. */
  stats(): { packets: number; lastPacketAt: number | null; statusFrames: number; lastStatusHex: string | null } {
    return { packets: this.packets, lastPacketAt: this.lastPacketAt, statusFrames: this.statusFrames, lastStatusHex: this.lastStatusHex }
  }

  /** True while the measured readout is running (resistance mode). */
  get measuring(): boolean {
    return this.measure !== null
  }

  async startSignal(): Promise<void> {
    if (!this.device?.gatt?.connected) throw new HeadbandError('protocol', 'not connected')
    if (this.transition) await this.transition
    const run = this.cancelMeasure()
    if (run) {
      // Resistance mode → signal mode before anything can record: the
      // vendor's stopResistanceData, then its startEEGStream (brainbit.js
      // 97–102, 73–88), with the reference adapter's spacing.
      await this.leaveResistMode(run)
      await this.resumeSignal()
    } else {
      await this.ensureStreaming()
    }
    this.streaming = true
  }

  async stopSignal(): Promise<void> {
    this.streaming = false
    if (this.measure) return // resistance mode: there is no signal stream to stop
    if (!this.estimating) await this.write(CMD_STOP).catch(() => undefined)
  }

  /**
   * Contact readings: measured when the headband answers the vendor's
   * resistance commands, otherwise estimated from the signal, exactly as
   * before R5.
   */
  async startResist(): Promise<void> {
    if (!this.device?.gatt?.connected) throw new HeadbandError('protocol', 'not connected')
    if (this.transition) await this.transition
    if (this.measuredWanted && !this.measuredFailed) {
      if (!this.measure) await this.startMeasured()
      return
    }
    await this.startEstimating()
  }

  /** The pre-R5 `startResist`, unchanged: stream signal and estimate from it. */
  private async startEstimating(): Promise<void> {
    await this.ensureStreaming()
    this.estimating = true
    if (this._info) this._info.estimatedQuality = true
    this.estimator = new SignalQualityEstimator(CLASSIC_CHANNELS, SAMPLING_RATE)
    this.resistTimer = setInterval(() => {
      if (!this.estimator) return
      this.emitter.emit('resist', { at: performance.now(), readings: this.estimator.readings(), estimated: true })
    }, RESIST_PERIOD_MS)
  }

  private stopEstimating(): void {
    this.estimating = false
    this.estimator = null
    if (this.resistTimer) clearInterval(this.resistTimer)
    this.resistTimer = null
  }

  async stopResist(): Promise<void> {
    if (this.transition) await this.transition
    const run = this.cancelMeasure()
    if (run) {
      await this.leaveResistMode(run)
      return
    }
    this.stopEstimating()
    if (!this.streaming) await this.write(CMD_STOP).catch(() => undefined)
  }

  /* ── measured resistance (classicResistance.ts) ──────────────────────── */

  /**
   * One attempt: STOP, settle, resist contact 1 direct (brainbit.js 112); the
   * cycle then drives itself from the status and RX notifications. Resolves on
   * the first full reading or on cancellation; on failure, falls back.
   */
  private async startMeasured(): Promise<void> {
    this.streaming = false
    let resolveFirst!: MeasureRun['resolveFirst']
    let rejectFirst!: MeasureRun['rejectFirst']
    const first = new Promise<'reading' | 'cancelled'>((resolve, reject) => {
      resolveFirst = resolve
      rejectFirst = reject
    })
    const run: MeasureRun = { cycle: new ResistanceCycle(), commanded: false, timer: null, inflight: Promise.resolve(), resolveFirst, rejectFirst, first }
    this.measure = run
    this.watch(run, MEASURE_FIRST_READING_MS, `no resistance reading within ${MEASURE_FIRST_READING_MS / 1000} s`)
    run.inflight = (async () => {
      await this.write(CMD_STOP)
      await delay(MODE_SETTLE_MS)
      if (this.measure !== run) return
      run.commanded = true
      await this.write(ResistanceCycle.first())
    })().catch((err) => this.measureFailed(run, `the resistance command failed: ${reasonOf(err)}`))
    try {
      await first
    } catch {
      await this.fallBack(run)
    }
  }

  private watch(run: MeasureRun, ms: number, why: string): void {
    if (run.timer) clearTimeout(run.timer)
    run.timer = setTimeout(() => this.measureFailed(run, why), ms)
  }

  private onResistPacket(run: MeasureRun, value: DataView): void {
    let out: CycleOutput
    try {
      out = run.cycle.onPacket(value)
    } catch (err) {
      this.measureFailed(run, `malformed resistance data: ${reasonOf(err)}`)
      return
    }
    if (out.ohms) this.measuredReading(run, out.ohms)
    if (out.command && this.measure === run) {
      const cmd = out.command
      run.inflight = run.inflight
        .then(() => (this.measure === run ? this.write(cmd) : undefined))
        .catch((err) => this.measureFailed(run, `the resistance command failed: ${reasonOf(err)}`))
    }
  }

  /** Ω → kΩ once, at ingress, as calibrations' `_resist_cb` does; +∞ (not measurable) → no reading. */
  private measuredReading(run: MeasureRun, ohms: Record<ResistContact, number>): void {
    const readings: Record<string, number | null> = {}
    for (const ch of CLASSIC_CHANNELS) {
      const v = ohms[ch]
      if (typeof v !== 'number' || Number.isNaN(v) || v < 0) {
        this.measureFailed(run, `malformed resistance reading for ${ch}: ${v}`)
        return
      }
      readings[ch] = Number.isFinite(v) ? v / 1000 : null
    }
    if (this._info) this._info.estimatedQuality = false
    this.watch(run, MEASURE_GAP_MS, `no resistance reading for ${MEASURE_GAP_MS / 1000} s`)
    this.emitter.emit('resist', { at: performance.now(), readings, estimated: false })
    const resolve = run.resolveFirst
    run.resolveFirst = run.rejectFirst = null
    resolve?.('reading')
  }

  /** The measured attempt failed: the estimate takes over for the rest of the connection. */
  private measureFailed(run: MeasureRun, why: string): void {
    if (this.measure !== run) return
    this.measure = null
    if (run.timer) clearTimeout(run.timer)
    run.timer = null
    this.measuredFailed = true
    console.warn('[brainbit] measured resistance failed; estimating from the signal instead:', why)
    const reject = run.rejectFirst
    run.resolveFirst = run.rejectFirst = null
    // Before the first reading, `startMeasured` is waiting and falls back itself.
    if (reject) reject(new HeadbandError('protocol', why))
    else void this.fallBack(run).catch((err) => console.warn('[brainbit] the signal estimate did not start:', reasonOf(err)))
  }

  /** Leave resistance mode and start the pre-R5 estimate. Other calls wait for it. */
  private fallBack(run: MeasureRun): Promise<void> {
    const work = (async () => {
      await this.leaveResistMode(run)
      await this.startEstimating()
    })()
    const settled = work.then(
      () => undefined,
      () => undefined,
    )
    this.transition = settled
    void settled.then(() => {
      if (this.transition === settled) this.transition = null
    })
    return work
  }

  /** Stop routing frames to the cycle; wake a pending first-reading wait. */
  private cancelMeasure(): MeasureRun | null {
    const run = this.measure
    if (!run) return null
    this.measure = null
    if (run.timer) clearTimeout(run.timer)
    run.timer = null
    const resolve = run.resolveFirst
    run.resolveFirst = run.rejectFirst = null
    resolve?.('cancelled')
    return run
  }

  /** The vendor's stopResistanceData (brainbit.js 97–102): STOP, then the settle. */
  private async leaveResistMode(run: MeasureRun): Promise<void> {
    await Promise.race([run.inflight, delay(INFLIGHT_GRACE_MS)])
    await this.write(CMD_STOP).catch(() => undefined)
    await delay(MODE_SETTLE_MS)
  }

  /**
   * The vendor's startEEGStream (brainbit.js 80: `commands.signal`, our
   * `CMD_START`), confirmed by a frame arriving; the reference adapter's one
   * STOP/START retry if none does. Throws when the headband stays silent, so
   * the page reports it rather than recording nothing.
   */
  private async resumeSignal(): Promise<void> {
    for (let attempt = 1; attempt <= 2; attempt++) {
      if (attempt > 1) {
        console.warn('[brainbit] no RX packet 2 s after START; sending STOP/START once more')
        await this.write(CMD_STOP)
        await delay(MODE_SETTLE_MS)
      }
      const before = this.packets
      await this.write(CMD_START)
      for (let waited = 0; waited < SIGNAL_CONFIRM_MS && this.packets === before; waited += 50) await delay(50)
      if (this.packets !== before) return
    }
    throw new HeadbandError('protocol', 'The headband did not return to its signal after the contact check.')
  }

  private async teardown(): Promise<void> {
    this.cancelMeasure()
    this.stopEstimating()
    this.streaming = false
    try {
      this.rx?.removeEventListener('characteristicvaluechanged', this.onPacket)
      this.status?.removeEventListener('characteristicvaluechanged', this.onStatus)
      if (this.device?.gatt?.connected) {
        await this.write(CMD_STOP).catch(() => undefined)
        await this.rx?.stopNotifications().catch(() => undefined)
        await this.status?.stopNotifications().catch(() => undefined)
        this.device.gatt.disconnect()
      }
    } finally {
      this.device?.removeEventListener('gattserverdisconnected', this.onDisconnected)
      this.device = null
      this.tx = this.rx = this.status = null
    }
  }

  async disconnect(): Promise<void> {
    await this.teardown()
    this._info = null
    this.emitter.emit('status', { at: performance.now(), connected: false, battery: null })
  }
}
